import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import SectionHeader from "../Components/SectionHeader";
import "./MyGoqii.css";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import { navigateWithFai, triggerShare } from "../utils/Utils";

function makeCircularDataUrl(imgUrl, size = 30) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, size, size);
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        const ratio = Math.max(size / img.width, size / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        const dx = (size - w) / 2;
        const dy = (size - h) / 2;
        ctx.drawImage(img, dx, dy, w, h);
        ctx.restore();
        const dataUrl = canvas.toDataURL("image/png");
        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = (e) => reject(e);
    img.src = imgUrl;
  });
}

function makeAvatarSvgDataUrl(innerImageUrl, outerSize = 34, ringWidth = 3, ringColor = "rgba(255,255,255,0.98)") {
  const innerSize = outerSize - ringWidth * 2;
  const cx = outerSize / 2;
  const cy = outerSize / 2;
  const clipR = innerSize / 2;
  const strokeR = clipR + ringWidth / 2;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${outerSize}" height="${outerSize}" viewBox="0 0 ${outerSize} ${outerSize}">
      <defs>
        <clipPath id="a">
          <circle cx="${cx}" cy="${cy}" r="${clipR}" />
        </clipPath>
        <filter id="s" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="0.6" flood-color="#000" flood-opacity="0.06"/>
        </filter>
      </defs>
      <g filter="url(#s)">
        <image href="${innerImageUrl}" x="${ringWidth}" y="${ringWidth}" width="${innerSize}" height="${innerSize}" preserveAspectRatio="xMidYMid slice" clip-path="url(#a)" />
        <circle cx="${cx}" cy="${cy}" r="${strokeR}" fill="none" stroke="${ringColor}" stroke-width="${ringWidth}" />
      </g>
    </svg>`.trim();
  const encoded = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
  return `data:image/svg+xml;utf8,${encoded}`;
}

// Shimmer helpers (same pattern as other lifestyle cards)
const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const MyGoqiiSkeleton = () => {
  useEffect(() => {
    ensureShimmerKeyframes();
  }, []);

  return (
    <div className="mg-wrapper">
      {/* SectionHeader skeleton */}
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          {/* Icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 32,
              height: 32,
              borderRadius: 999,
            }}
          />

          {/* Title + subtitle */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div
              style={{
                ...shimmerBaseStyle,
                height: 18,
                width: "60%",
                borderRadius: 8,
              }}
            />
            <div
              style={{
                ...shimmerBaseStyle,
                height: 14,
                width: "40%",
                borderRadius: 8,
              }}
            />
          </div>

          {/* Info icon */}
          <div
            style={{
              ...shimmerBaseStyle,
              width: 20,
              height: 20,
              borderRadius: 999,
            }}
          />
        </div>
      </div>

      {/* Chart container skeleton */}
      <div className="mg-highchart-container">
        <div
          style={{
            ...shimmerBaseStyle,
            height: 190,
            borderRadius: 16,
            margin: "8px 4px 0 4px",
          }}
        />
      </div>

      {/* Footer skeleton */}
      <div className="mg-footer">
        <div
          style={{
            ...shimmerBaseStyle,
            height: 16,
            width: "70%",
            borderRadius: 8,
            margin: "0 auto",
          }}
        />
      </div>
    </div>
  );
};

export default function MyGoqii({ shareConfig } = {}) {
  const [data, setData] = useState(null);
  const [avatarDataProcessed, setAvatarDataProcessed] = useState(null);
  const chartRef = useRef(null);


  useEffect(() => {
    const { encryptedId } = getUrlParams();
    if (!encryptedId) return;

    lifestyleApi
      .mygoqiiData(encryptedId)
      .then((res) => {
        if (res?.data?.code === 200) {
          setData(res.data.data);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (chartRef.current?.chart) {
      setTimeout(() => chartRef.current.chart.reflow(), 50);
    }
  }, [data, avatarDataProcessed]);

  useEffect(() => {
    if (!data) {
      setAvatarDataProcessed(null);
      return;
    }

    let cancelled = false;

    function computeMarkerSize() {
      const cw = chartRef.current?.chart?.chartWidth || window.innerWidth;
      if (cw < 360) return 26;
      if (cw < 520) return 30;
      return 34;
    }

    async function buildAvatars() {
      const markerSize = computeMarkerSize();

      const avatarPoints = data.series
        .filter((pt) => pt.rp > 0)
        .map((pt) => ({ original: pt, image: pt.image, x: pt.x, y: pt.y, markerSize }));

      if (avatarPoints.length === 0) {
        if (!cancelled) setAvatarDataProcessed([]);
        return;
      }

      try {
        const results = await Promise.all(avatarPoints.map(async (pt) => {
          const ringColor = (pt.original && pt.original.color) ? pt.original.color : "rgba(255,255,255,0.98)";
          try {
            const innerSize = Math.max(20, Math.round(pt.markerSize - 6));
            const png = await makeCircularDataUrl(pt.image, innerSize);
            const svgUrl = makeAvatarSvgDataUrl(png, pt.markerSize, 3, ringColor);
            return {
              x: pt.x,
              y: pt.y,
              marker: { symbol: `url(${svgUrl})`, width: pt.markerSize, height: pt.markerSize },
              markerSize: pt.markerSize,
              _maskedFailed: false,
              orig: pt.original,
            };
          } catch (err) {
            console.warn("avatar masking failed for", pt.image, err);
            const svgUrl = makeAvatarSvgDataUrl(pt.image, pt.markerSize, 3, ringColor);
            return {
              x: pt.x,
              y: pt.y,
              marker: { symbol: `url(${svgUrl})`, width: pt.markerSize, height: pt.markerSize },
              markerSize: pt.markerSize,
              _maskedFailed: true,
              orig: pt.original,
            };
          }
        }));

        if (!cancelled) setAvatarDataProcessed(results);
      } catch (err) {
        console.warn("avatar processing failed", err);
        if (!cancelled) {
          setAvatarDataProcessed(avatarPoints.map((pt) => {
            const ringColor = (pt.original && pt.original.color) ? pt.original.color : "rgba(255,255,255,0.98)";
            return {
              x: pt.x,
              y: pt.y,
              marker: { symbol: `url(${pt.image})`, width: pt.markerSize, height: pt.markerSize },
              markerSize: pt.markerSize,
              _maskedFailed: true,
              orig: pt.original,
              ringColor,
            };
          }));
        }
      }
    }

    buildAvatars();

    function onResize() {
      clearTimeout(onResize._t);
      onResize._t = setTimeout(() => buildAvatars(), 120);
    }
    window.addEventListener("resize", onResize);

    const chart = chartRef.current?.chart;
    if (chart && typeof window.ResizeObserver === "function") {
      try {
        const container = chart.container.parentNode;
        if (container) {
          const ro = new ResizeObserver(() => onResize());
          ro.observe(container);
          onResize._ro = ro;
        }
      } catch (e) { }
    }

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      if (onResize._ro) onResize._ro.disconnect();
    };
  }, [data]);

  if (!data) return <MyGoqiiSkeleton />;

  const columnData = data.series.map((pt) => ({ x: pt.x, y: pt.y, color: pt.color }));

  const avatarData = (avatarDataProcessed || []).map((pt) => ({
    x: pt.x,
    y: pt.y,
    marker: pt.marker,
    markerSize: pt.markerSize,
    _maskedFailed: pt._maskedFailed,
  }));

  const options = {
    chart: {
      type: "column",
      height: 120,
      backgroundColor: "transparent",
      spacing: [2, 6, 6, 12],
      style: {},
      events: {
        render() {
          const radius = 8;
          function roundedRectPath(x, y, w, h, r) {
            const R = Math.min(r, w / 2, h / 2);
            return [
              `M${x + R},${y}`,
              `h${w - 2 * R}`,
              `a${R},${R} 0 0 1 ${R},${R}`,
              `v${h - 2 * R}`,
              `a${R},${R} 0 0 1 ${-R},${R}`,
              `h${- (w - 2 * R)}`,
              `a${R},${R} 0 0 1 ${-R},${-R}`,
              `v${- (h - 2 * R)}`,
              `a${R},${R} 0 0 1 ${R},${-R}`,
              `z`,
            ].join(" ");
          }

          this.series.forEach((series) => {
            if (series.type !== "column") return;
            series.points.forEach((pt) => {
              if (!pt || !pt.graphic) return;
              const el = pt.graphic.element;
              if (!el) return;
              if (el.tagName === "rect") {
                el.setAttribute("rx", radius);
                el.setAttribute("ry", radius);
              } else if (el.tagName === "path") {
                const sa = pt.shapeArgs;
                if (sa && sa.x != null) {
                  const d = roundedRectPath(sa.x, sa.y, sa.width, sa.height, radius);
                  el.setAttribute("d", d);
                } else {
                  try {
                    const bbox = pt.graphic.getBBox();
                    const d = roundedRectPath(bbox.x, bbox.y, bbox.width, bbox.height, radius);
                    el.setAttribute("d", d);
                  } catch (e) { }
                }
              }
            });
          });

          try {
            this.series.forEach((series) => {
              if (series.type !== "scatter") return;
              series.points.forEach((pt) => {
                if (!pt || !pt.graphic) return;
                const needsClip = pt.options && pt.options._maskedFailed;
                const imgEl = pt.graphic.element;
                if (needsClip && imgEl && !imgEl._svgClipApplied) {
                  let bbox;
                  try {
                    bbox = pt.graphic.getBBox();
                  } catch (err) {
                    const sa = pt.shapeArgs || {};
                    bbox = { x: sa.x || 0, y: sa.y || 0, width: sa.width || (sa.r || 30), height: sa.height || (sa.r || 30) };
                  }
                  const r = Math.min(bbox.width, bbox.height) / 2;
                  const cx = bbox.x + bbox.width / 2;
                  const cy = bbox.y + bbox.height / 2;
                  const clipId = `avatar-clip-s${series.index}-p${pt.index}`;
                  if (!document.getElementById(clipId)) {
                    let defsEl = this.renderer.defs;
                    if (!defsEl) defsEl = this.renderer.createElement("defs").add(this.renderer.box);
                    const clipPath = this.renderer.createElement("clipPath").attr({ id: clipId }).add(defsEl);
                    this.renderer.createElement("circle").attr({ cx: cx, cy: cy, r: r }).add(clipPath);
                  }
                  imgEl.setAttribute("clip-path", `url(#${clipId})`);
                  imgEl._svgClipApplied = true;
                }

                try {
                  let markerBBox = null;
                  try {
                    markerBBox = pt.graphic && typeof pt.graphic.getBBox === "function" ? pt.graphic.getBBox() : null;
                  } catch (e) {
                    markerBBox = null;
                  }
                  const markerW = markerBBox?.width || (pt.options && pt.options.marker && pt.options.marker.width) || 34;
                  const markerH = markerBBox?.height || (pt.options && pt.options.marker && pt.options.marker.height) || markerW;
                  const columnSeries = this.series.find(s => (s.type === 'column' || s.options.type === 'column'));
                  const columnPoint = columnSeries && columnSeries.points ? columnSeries.points.find(p => p.x === pt.x) : null;
                  const columnW = (columnPoint && columnPoint.shapeArgs && columnPoint.shapeArgs.width) || 15;
                  const yShift = Math.round(markerH * 0.25);
                  const xShift = -Math.round((markerW - columnW) / 1.2);
                  const el = pt.graphic && pt.graphic.element;
                  if (el) {
                    el.setAttribute("transform", `translate(${xShift},${-yShift})`);
                  }
                } catch (e) { }
              });
            });
          } catch (e) { }
        },
      },
    },
    title: { text: null },
    xAxis: {
      minPadding: 0,
      maxPadding: 0,
      tickInterval: 100,
      lineWidth: 1,
      lineColor: "var(--border-subtle)",
      tickColor: "var(--border-subtle)",
      tickLength: 6,
      labels: { style: { color: "var(--text-primary)", fontSize: "12px", fontFamily: "Open Sans, sans-serif" }, y: 14 },
      gridLineWidth: 0,
      offset: 0,
    },
    yAxis: {
      min: 0,
      tickInterval: 50,
      title: { text: null },
      labels: {
        enabled: false,
        x: -8
      },
      gridLineWidth: 0,
      lineWidth: 0,
    },
    legend: { enabled: false },
    tooltip: {
      enabled: false,
      headerFormat: "",
      pointFormatter() {
        return `<b>x:</b> ${this.x}<br/><b>y:</b> ${this.y}`;
      },
      useHTML: true,
    },
    plotOptions: {
      column: {
        grouping: false,
        borderWidth: 0,
        pointPadding: 0,
        groupPadding: 0,
        pointWidth: 15,
        borderRadius: 4,
        zIndex: 1,
      },
      scatter: {
        enableMouseTracking: false,
        marker: { enabled: true },
        dataLabels: { enabled: false },
        zIndex: 6,
      },
    },
    series: [
      { name: "Activity", data: columnData, type: "column", zIndex: 1 },
      { type: "scatter", data: avatarData, zIndex: 6 },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="mg-wrapper" style={{}}>
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/42216_1764052623.png"
        title={data.title}
        subtitle={data.heading}
        showInfo={data.showInfo}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        onInfoClick={() => {
          navigateWithFai("MyGoqii Life", data.infoUrl);
        }}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />

      <div className="mg-highchart-container">
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
      </div>

      <div className="mg-footer" style={{ fontSize: "9px" }}>{data.subText}</div>
    </div>
  );
}
